import { subscribe, getState, openModalForEdit, setMemoriamSort } from '../state/store.js';
import { formatDateForDisplayValue, getDateSortValue } from '../utils/dates.js';
import { getHighestCharacterLevel } from '../utils/levels.js';
import { canEditCharacterAsViewer } from '../utils/players.js';

const memoriamGrid = document.getElementById('memoriamGrid');
const memoriamSortBar = document.getElementById('memoriamSortBar');

const SORT_ASC = '▲'; // ▲
const SORT_DESC = '▼'; // ▼

const isDeadStatus = (status) => String(status || '').trim().toLowerCase() === 'dead';

const escapeHTML = (value = '') =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const getSortValue = (character, key) => {
  switch (key) {
    case 'name':
      return (character.name || '').toLowerCase();
    case 'level':
      return getHighestCharacterLevel(character.level, 0);
    case 'deathday':
    default:
      return getDateSortValue(character.deathday);
  }
};

const sortDead = (dead, sort) => {
  const dir = sort.direction === 'asc' ? 1 : -1;
  return dead.sort((a, b) => {
    const va = getSortValue(a, sort.key);
    const vb = getSortValue(b, sort.key);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });
};

const renderCard = (character, editable) => {
  const classRaceLine = [character.race, character.class].filter(Boolean).join(' ');
  const fellAt = character.level ? ` — fell at Level ${escapeHTML(character.level)}` : '';
  const cause = character.cause_of_death
    ? `<p class="memoriam-cause">"${escapeHTML(character.cause_of_death)}"</p>`
    : '';
  const diedDate = character.deathday ? formatDateForDisplayValue(character.deathday) : 'Unknown date';
  const player = character.player || 'Unknown Player';
  const plateClass = character.portrait_url ? 'plate dead has-image' : 'plate dead';
  const plateStyleAttr = character.portrait_url
    ? ` style="background-image:url('${escapeHTML(character.portrait_url)}')"`
    : '';
  const editBtn = editable
    ? `<div class="memoriam-card-actions">
      <button type="button" class="btn small outline" data-action="edit" data-id="${character.id}">Edit</button>
    </div>`
    : '';

  return `<article class="memoriam-card" data-id="${character.id}">
    <div class="${plateClass}"${plateStyleAttr}><span class="plate-hint">Portrait</span></div>
    <div class="memoriam-main">
      <h3>${escapeHTML(character.name || 'Unnamed')}</h3>
      <p class="memoriam-class-line">${escapeHTML(classRaceLine)}${fellAt}</p>
      ${cause}
      <p class="memoriam-meta">Died ${escapeHTML(diedDate)} &middot; played by ${escapeHTML(player)}</p>
      ${editBtn}
    </div>
  </article>`;
};

const updateSortIndicators = (sort) => {
  if (!memoriamSortBar) return;
  memoriamSortBar.querySelectorAll('.sort-btn').forEach((btn) => {
    if (btn.dataset.sort === sort.key) {
      btn.classList.add('active');
      btn.setAttribute('data-indicator', sort.direction === 'asc' ? SORT_ASC : SORT_DESC);
    } else {
      btn.classList.remove('active');
      btn.setAttribute('data-indicator', '');
    }
  });
};

const renderMemoriam = () => {
  if (!memoriamGrid) return;
  const state = getState();
  const { characters, memoriamSort } = state;
  const dead = sortDead(
    characters.filter((c) => isDeadStatus(c.status)).slice(),
    memoriamSort
  );
  memoriamGrid.innerHTML = dead.length
    ? dead.map((character) => renderCard(character, canEditCharacterAsViewer(character, state))).join('')
    : '<p class="placeholder">No losses yet — long may it last.</p>';
  updateSortIndicators(memoriamSort);
};

export const initMemoriam = () => {
  memoriamGrid?.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-action="edit"]');
    if (!btn) return;
    event.stopPropagation();
    openModalForEdit(Number(btn.dataset.id));
  });

  memoriamSortBar?.addEventListener('click', (event) => {
    const btn = event.target.closest('.sort-btn');
    if (!btn) return;
    setMemoriamSort(btn.dataset.sort);
  });

  subscribe(renderMemoriam);
  renderMemoriam();
};
